import * as THREE from 'three';
import { globalStores } from '@/editor/stores';
import { worldPosToRenderPos } from '.';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { EColumnType } from '@/editor/data/model/model';
import { isNumber } from '@/utils/types/assert';
import { getLinearDimensionRulerLine } from '@/editor/data/dimension/dimension';
import { IShapePoint2d } from '../types/shape';
import { ArcSeg2d } from '../math/arcSeg2d';
import { Point2dUtils } from '../math/Point2dUtils';
import { thresholdValue } from '../math/const';

function convertWallOutlinePathToPoint(path: IShapePoint2d[]): IPoint2d[] {
  const result: IPoint2d[] = [];
  for (let i = 0; i < path.length; i++) {
    const point = path[i];
    if (point.center) {
      const nextPoint = path[(i + 1) % path.length];
      const arc = new ArcSeg2d({
        start: point,
        end: nextPoint,
        center: point.center,
        clockwise: point.clockwise ?? true
      });
      result.push(...arc.getPoints());
    } else {
      result.push(point);
    }
  }
  return result;
}

export function expandByWallOutlines(box: THREE.Box3) {
  globalStores.connectedWallOutlines.forEach(({ path }) => {
    convertWallOutlinePathToPoint(path).forEach((point) => {
      const realPoint = worldPosToRenderPos({ ...point, z: globalStores.paramStore.layerHeight });
      box.expandByPoint(new THREE.Vector3(realPoint.x, realPoint.y, 0));
      box.expandByPoint(new THREE.Vector3(realPoint.x, realPoint.y, realPoint.z));
    });
  });
}

/**
 * 获取整个场景包围盒
 *
 * 计算包含：
 *  - 直墙：起点和终点
 *  - 弧墙：弧形的取样点
 *  - 家具：四个顶点
 *  - 柱子：四个顶点
 *  - 尺寸标注：源线段两端点和偏移后尺寸线两端点
 * @returns 场景包围盒
 */
export function computeEdgeBoundingBox(): THREE.Box3 {
  const box = new THREE.Box3();

  // 将墙体数据加入边界计算
  expandByWallOutlines(box);

  globalStores.modelStore.getFurnitureDatas().forEach((furnitureData) => {
    computeRectangleVertices({
      center: furnitureData.center,
      size: {
        width: furnitureData.width,
        height: furnitureData.thickness
      },
      rotation: furnitureData.rotation
    }).forEach((point) => {
      const position = worldPosToRenderPos(point);
      box.expandByPoint(new THREE.Vector3(position.x, position.y, 0));
    });
  });

  globalStores.structStore.getColumnDatas().forEach((columnData) => {
    computeRectangleVertices({
      center: columnData.center,
      size:
        columnData.type === EColumnType.cylinder
          ? columnData.radius * 2
          : { width: columnData.width, height: columnData.thickness },
      rotation: columnData.rotation
    }).forEach((point) => {
      const position = worldPosToRenderPos(point);
      box.expandByPoint(new THREE.Vector3(position.x, position.y, 0));
    });
  });

  globalStores.dimensionStore.getLinearDimList().forEach((linearDimension) => {
    const { start, end } = linearDimension;
    // 退化（零长度）标注无法确定偏移方向，跳过避免 NaN 污染包围盒
    if (Point2dUtils.distanceTo(start, end) < thresholdValue) return;

    const rulerLine = getLinearDimensionRulerLine(linearDimension);
    [start, end, rulerLine.start, rulerLine.end].forEach((point) => {
      const position = worldPosToRenderPos(point);
      box.expandByPoint(new THREE.Vector3(position.x, position.y, 0));
    });
  });

  return box;
}

/**
 * 根据中心、尺寸和旋转角度计算矩形顶点
 * @param params.center 矩形中心点
 * @param params.size 矩形尺寸，宽高对象或单一数字（表示宽高相等）
 * @param params.rotation 矩形旋转角度，单位：度
 * @returns 矩形四个顶点坐标数组
 */
export function computeRectangleVertices(params: {
  center: IPoint2d;
  size: { width: number; height: number } | number;
  rotation: number;
}): IPoint2d[] {
  const { center, size, rotation } = params;
  const halfWidth = isNumber(size) ? size / 2 : size.width / 2;
  const halfHeight = isNumber(size) ? size / 2 : size.height / 2;
  const rad = (rotation * Math.PI) / 180;

  const corners = [
    { x: -halfWidth, y: -halfHeight },
    { x: halfWidth, y: -halfHeight },
    { x: halfWidth, y: halfHeight },
    { x: -halfWidth, y: halfHeight }
  ];

  return corners.map((corner) => {
    // 如果没有旋转角度，直接返回计算后的坐标
    if (rotation === 0) {
      return {
        x: center.x + corner.x,
        y: center.y + corner.y
      };
    }

    // 旋转坐标后返回
    const rotatedX = corner.x * Math.cos(rad) - corner.y * Math.sin(rad);
    const rotatedY = corner.x * Math.sin(rad) + corner.y * Math.cos(rad);
    return {
      x: center.x + rotatedX,
      y: center.y + rotatedY
    };
  });
}
